import React from 'react';

import { Button, Group, LoadingOverlay, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import Drawer from 'components/Drawer';
import PackageMultiSelect from 'components/form-fields/auto-complete-fields/PackageMultiSelect';
import useCreatePackageCategory from 'hooks/packageCategory/useCreatePackageCategory';
import usePackageCategory from 'hooks/packageCategory/usePackageCategory';
import useUpdatePackageCategory from 'hooks/packageCategory/useUpdatePackageCategory';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  name: string;
  packages: string[];
}

interface IDrawerPackageCategoryFormProps {
  formId: string;
  title: JSX.Element;
  opened: boolean;
  type: 'create' | 'edit';
  packageCategoryId?: string;
  closeHandler: () => void;
  permission?: boolean;
}

const DrawerPackageCategoryForm = ({
  formId,
  title,
  opened,
  type,
  packageCategoryId,
  closeHandler,
  permission = true,
}: IDrawerPackageCategoryFormProps): JSX.Element => {
  const createMutation = useCreatePackageCategory();
  const updateMutation = useUpdatePackageCategory();

  const { data, isLoading, isFetching, isSuccess } = usePackageCategory(packageCategoryId);

  const form = useForm<FormValues>({
    initialValues: { name: '', packages: [] },

    validate: {
      name: value => (!value || value.length > 120 ? 'Name must be between 1-120 characters' : null),
    },
  });

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleSubmit = (payload: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();
    if (type === 'edit' && packageCategoryId) {
      updateMutation.mutate(
        {
          packageCategoryId,
          name: payload.name,
          packages: payload.packages,
        },
        {
          onError: error => {
            errorNotification('Error updating package category', error.response?.data.message);
          },
          onSuccess: () => {
            successNotification('Package category updated', 'Package category updated successfully.');
            handleCloseAndReset();
          },
        },
      );
      return;
    }
    createMutation.mutate(
      {
        name: payload.name,
        packages: payload.packages,
        packageCategoryId: uuidv4(),
      },
      {
        onSuccess: () => {
          successNotification('Package category created', 'Package category created successfully.');
          handleCloseAndReset();
        },
        onError: error => {
          errorNotification('Error creating package category', error.response?.data.message);
        },
      },
    );
  };

  useShallowEffect(() => {
    if (type === 'edit' && isSuccess && data.data) {
      form.setValues({
        name: data.data.name,
        packages: data.data.packages.map(pkg => pkg.packageId),
      });
    }
  }, [data, isSuccess, type, isFetching, isLoading]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      title={title}
      size='lg'
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            type='submit'
            form={formId}
            disabled={!permission || createMutation.isLoading || isLoading || isFetching}
            loading={createMutation.isLoading}
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay visible={createMutation.isLoading} />
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            required
            label='Name'
            placeholder='Type a custom name for package category'
            {...form.getInputProps('name')}
          />
          <PackageMultiSelect
            label='Packages'
            placeholder='Select packages to include in this category'
            {...form.getInputProps('packages')}
          />
        </Stack>
      </form>
    </Drawer>
  );
};
export default DrawerPackageCategoryForm;
